
import {
  CargoV2SearchFlight,
  CargoV2SearchFlightItinerary,
  CargoV2FlightDetail,
  CargoV2Data
} from "@/domain/entities/CargoV2";
import moment from "moment";
import { Vue, Options, prop } from "vue-class-component";

interface ItinerariesMap extends CargoV2SearchFlightItinerary {
  prevArrivalDate?: string;
}

class Props {
  isSelected = prop<boolean>({
    default: false,
    type: Boolean,
  });
  flightDetailData = prop<CargoV2Data>({
    default: new CargoV2Data(),
    type: CargoV2Data,
  });
}

@Options({})
export default class CardFlightDetail extends Vue.with(Props) {
  isTransitOpen = false;
  isSelected = false;
  flight = new CargoV2SearchFlight()

  mounted() {
    this.setFlightData()
  }

  setFlightData() {
    this.flight.departureDate = this.flightDetailData.departureDate
    this.flight.arrivalDate = this.flightDetailData.arrivalDate
    this.flight.totalTravelTimeMin = this.getTravelTimeInMinutes(this.flightDetailData.flightsDetail[0].cfEstDepartureTime, this.flightDetailData.flightsDetail[this.flightDetailData.flightsDetail.length-1].cfEstArrivalTime)
    this.flight.totalLeg = this.flightDetailData.flightsDetail.length
    this.flight.originAirportCode = this.flightDetailData.airportOriginCityCode
    this.flight.destinationAirportCode = this.flightDetailData.airportDestinationCityCode
    this.flightDetailData.flightsDetail.forEach((item: CargoV2FlightDetail) => {
      this.flight.itineraries.push(new CargoV2SearchFlightItinerary({
        flightNo: item.cfNumber,
        departureDate: item.cfEstDepartureTime,
        arrivalDate: item.cfEstArrivalTime,
        originAirportCode: item.cfAirportOrigin,
        destinationAirportCode: item.cfAirportDestination
      }))
    })
  }

  getTravelTimeInMinutes(departureDate: string, arrivalDate: string) {
    const departure = moment(departureDate);
    const arrival = moment(arrivalDate);

    const diffMinutes = arrival.diff(departure, "minutes");

    return diffMinutes;
  }

  flightIcon(flightNoData: string) {
    const flightNo = flightNoData.toUpperCase();
    if (flightNo.startsWith("IW")) {
      return require("@/app/ui/assets/svg/flight-IW-wings-circle.svg");
    }
    if (flightNo.startsWith("ID")) {
      return require("@/app/ui/assets/svg/flight-ID-batik-circle.svg");
    }
    if (flightNo.startsWith("IU")) {
      return require("@/app/ui/assets/svg/flight-IU-super-airjet-circle.svg");
    }
    return require("@/app/ui/assets/svg/flight-JT-lion-circle.svg");
  }
  get lowestTransitCapacity() {
    return this.flight.itineraries.sort((a, b) => a.remainingCapacity - b.remainingCapacity)[0] ?? null;
  }
  toggleTransit(event: Event) {
    if(this.flight.itineraries.length > 1) {
      this.isTransitOpen = !this.isTransitOpen;
      event.stopPropagation();
    }
  }
  get firstFlightNo() {
    return this.flight.itineraries[0]?.flightNo ?? "";
  }
  get isGridLayout() {
    return this.flight.itineraries.length > 4;
  }
  get transitGridRows() {
    const items = [
      ...this.flight.itineraries.map((transit, index) => ({
        isDestination: false,
        isEmpty: false,
        transit,
        index,
      })),
      {
        isDestination: true,
        isEmpty: false,
        transit: this.flight.itineraries[this.flight.itineraries.length - 1],
        index: -1,
      },
    ];
    const rows = [];
    const firstRow = items.slice(0, 4) as typeof items;
    while (firstRow.length < 4) {
      firstRow.push({ isEmpty: true, isDestination: false, transit: null as any, index: -1 });
    }
    rows.push(firstRow);
    for (let i = 4; i < items.length; i += 3) {
      const row = items.slice(i, i + 3) as typeof items;
      while (row.length < 3) {
        row.push({ isEmpty: true, isDestination: false, transit: null as any, index: -1 });
      }
      rows.push(row);
    }
    return rows;
  }
  get transitLabel() {
    if (this.flight.itineraries.length <= 1) return this.$t("cargoV2.components.cardFlight.direct");
    const count = this.flight.itineraries.length - 1;
    return this.$t("cargoV2.components.cardFlight.transit", { count });
  }
  get departureHours() {
    const totalMinutes = moment(this.flight.departureDate).diff(
      moment(),
      "minutes",
    );
    return Math.max(0, Math.floor(totalMinutes / 60));
  }
  get departureMinutes() {
    const totalMinutes = moment(this.flight.departureDate).diff(
      moment(),
      "minutes",
    );
    return Math.max(0, totalMinutes % 60);
  }
  get arrivalDayDiff(): number {
    if (!this.flightDetailData.flightsDetail?.[0]?.cfEstDepartureTime || !this.flightDetailData.flightsDetail?.[this.flightDetailData.flightsDetail?.length-1]?.cfEstArrivalTime) return 0;
    return moment(this.flightDetailData.flightsDetail?.[this.flightDetailData.flightsDetail?.length-1]?.cfEstArrivalTime)
      .startOf("day")
      .diff(moment(this.flightDetailData.flightsDetail?.[0]?.cfEstDepartureTime).startOf("day"), "days");
  }
  formatDateShort(date: string) {
    return date
      ? moment(date)
          .locale(this.$i18n.locale)
          .format("ddd, DD MMM")
      : "";
  }
  formatDateTransit(date: string) {
    return date ? moment(date).format("DD MMM") : "";
  }
  formatDuration(totalMinutes: number) {
    if (totalMinutes <= 0) return this.$t("cargoV2.components.manualInput.flightDuration.minutes", { count: 0 });
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    if (hours === 0) return this.$t("cargoV2.components.manualInput.flightDuration.minutes", { count: mins });
    if (mins === 0) return this.$t("cargoV2.components.manualInput.flightDuration.hours", { count: hours });
    return this.$t("cargoV2.components.manualInput.flightDuration.hoursMinutes", { hours, minutes: mins });
  }
  formatTime(time: string) {
    return moment(time).format("HH:mm");
  }

  handleClickCard() {
    this.$emit("select", this.flight);
  }
}
